/**
 * CancellationFlow Component
 *
 * Multi-step cancellation flow with:
 * 1. Reason collection
 * 2. Retention offer (discount)
 * 3. Final confirmation
 */

import React, { useState } from 'react';
import { handleError } from '@/lib/errors';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Modal,
  TextInput,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import Animated, { SlideInUp, FadeIn } from 'react-native-reanimated';
import {
  X,
  MessageSquare,
  Gift,
  AlertTriangle,
  ChevronRight,
  Check,
  Sparkles,
} from 'lucide-react-native';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useKeyboardVisible } from '@/lib/useKeyboardVisible';

// ============================================================================
// Types
// ============================================================================

export interface CancellationFlowProps {
  visible: boolean;
  currentTier: string;
  currentPeriodEnd?: string;
  onCancel: (reason: string, feedback?: string) => Promise<void>;
  onAcceptOffer?: (discountCode: string) => Promise<void>;
  onClose: () => void;
  discountPercent?: number;
}

type Step = 'reason' | 'offer' | 'confirm';

interface CancellationReason {
  id: string;
  label: string;
  icon: React.ReactNode;
  offerDiscount: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const CANCELLATION_REASONS: CancellationReason[] = [
  {
    id: 'too_expensive',
    label: 'Too expensive',
    icon: null,
    offerDiscount: true,
  },
  {
    id: 'not_using',
    label: "I'm not using it enough",
    icon: null,
    offerDiscount: true,
  },
  {
    id: 'missing_features',
    label: 'Missing features I need',
    icon: null,
    offerDiscount: false,
  },
  {
    id: 'found_alternative',
    label: 'Found a better alternative',
    icon: null,
    offerDiscount: false,
  },
  {
    id: 'temporary_pause',
    label: 'Just need a break',
    icon: null,
    offerDiscount: true,
  },
  {
    id: 'other',
    label: 'Other reason',
    icon: null,
    offerDiscount: false,
  },
];

// ============================================================================
// Component
// ============================================================================

export function CancellationFlow({
  visible,
  currentTier,
  currentPeriodEnd,
  onCancel,
  onAcceptOffer,
  onClose,
  discountPercent = 30,
}: CancellationFlowProps) {
  const colors = useThemeColors();
  const keyboardVisible = useKeyboardVisible();
  const [step, setStep] = useState<Step>('reason');
  const [selectedReason, setSelectedReason] = useState<CancellationReason | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const formattedEndDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'your billing period end';

  const handleSelectReason = (reason: CancellationReason) => {
    setSelectedReason(reason);
  };

  const handleContinue = () => {
    if (!selectedReason) return;

    // If reason qualifies for discount offer, show offer step
    if (selectedReason.offerDiscount && onAcceptOffer) {
      setStep('offer');
    } else {
      setStep('confirm');
    }
  };

  const handleAcceptOffer = async () => {
    if (!onAcceptOffer) return;

    setIsLoading(true);
    try {
      await onAcceptOffer(`STAY${discountPercent}`);
      onClose();
    } catch (error) {
      handleError(error, { context: 'CancellationFlow.handleAcceptOffer' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeclineOffer = () => {
    setStep('confirm');
  };

  const handleConfirmCancel = async () => {
    if (!selectedReason) return;

    setIsLoading(true);
    try {
      await onCancel(selectedReason.id, additionalFeedback || undefined);
      onClose();
    } catch (error) {
      handleError(error, { context: 'CancellationFlow.handleConfirmCancel' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setStep('reason');
    setSelectedReason(null);
    setAdditionalFeedback('');
    onClose();
  };

  const renderReasonStep = () => (
    <Animated.View entering={FadeIn.duration(300)}>
      <View style={styles.stepHeader}>
        <View
          style={[
            styles.stepIconContainer,
            { backgroundColor: colors.primary[50] },
          ]}
        >
          <MessageSquare size={24} color={colors.primary[600]} />
        </View>
        <Text style={[styles.stepTitle, { color: colors.text.primary }]}>
          We're sorry to see you go
        </Text>
        <Text style={[styles.stepSubtitle, { color: colors.text.secondary }]}>
          Help us improve by sharing why you're canceling
        </Text>
      </View>

      <View style={styles.reasonsContainer}>
        {CANCELLATION_REASONS.map((reason) => (
          <Pressable
            key={reason.id}
            onPress={() => handleSelectReason(reason)}
            style={({ pressed }) => [
              styles.reasonOption,
              {
                backgroundColor:
                  selectedReason?.id === reason.id
                    ? colors.primary[50]
                    : colors.surface.secondary,
                borderColor:
                  selectedReason?.id === reason.id
                    ? colors.primary[500]
                    : colors.border.light,
                opacity: pressed ? 0.8 : 1,
              },
            ]}
          >
            <Text
              style={[
                styles.reasonText,
                {
                  color:
                    selectedReason?.id === reason.id
                      ? colors.primary[700]
                      : colors.text.primary,
                },
              ]}
            >
              {reason.label}
            </Text>
            {selectedReason?.id === reason.id && (
              <Check size={18} color={colors.primary[600]} />
            )}
          </Pressable>
        ))}
      </View>

      {selectedReason?.id === 'other' && (
        <TextInput
          style={[
            styles.feedbackInput,
            {
              backgroundColor: colors.surface.secondary,
              borderColor: colors.border.light,
              color: colors.text.primary,
            },
          ]}
          placeholder="Tell us more..."
          placeholderTextColor={colors.text.tertiary}
          value={additionalFeedback}
          onChangeText={setAdditionalFeedback}
          multiline
          numberOfLines={3}
        />
      )}

      <View style={styles.buttonRow}>
        <AnimatedPressable
          onPress={handleClose}
          style={[
            styles.button,
            styles.secondaryButton,
            {
              backgroundColor: colors.surface.secondary,
            },
          ]}
        >
          <Text style={[styles.buttonText, { color: colors.text.secondary }]}>
            Never mind
          </Text>
        </AnimatedPressable>

        <AnimatedPressable
          onPress={handleContinue}
          disabled={!selectedReason}
          style={[
            styles.button,
            styles.primaryButton,
            {
              backgroundColor: selectedReason
                ? colors.primary[600]
                : colors.surface.tertiary,
            },
          ]}
        >
          <Text
            style={[
              styles.buttonText,
              { color: selectedReason ? '#fff' : colors.text.tertiary },
            ]}
          >
            Continue
          </Text>
          <ChevronRight
            size={18}
            color={selectedReason ? '#fff' : colors.text.tertiary}
          />
        </AnimatedPressable>
      </View>
    </Animated.View>
  );

  const renderOfferStep = () => (
    <Animated.View entering={SlideInUp.springify().damping(20)}>
      <View style={styles.stepHeader}>
        <View
          style={[
            styles.stepIconContainer,
            styles.offerIconContainer,
          ]}
        >
          <Gift size={28} color="#fff" />
        </View>
        <Text style={[styles.stepTitle, { color: colors.text.primary }]}>
          Wait! Here's a special offer
        </Text>
        <Text style={[styles.stepSubtitle, { color: colors.text.secondary }]}>
          We'd hate to lose you. How about {discountPercent}% off your next 3 months?
        </Text>
      </View>

      <View
        style={[
          styles.offerCard,
          {
            backgroundColor: colors.primary[50],
            borderColor: colors.primary[200],
          },
        ]}
      >
        <View style={styles.offerHeader}>
          <Sparkles size={20} color={colors.primary[600]} />
          <Text style={[styles.offerTitle, { color: colors.primary[700] }]}>
            Exclusive Retention Offer
          </Text>
        </View>
        <Text style={[styles.offerDiscount, { color: colors.primary[600] }]}>
          {discountPercent}% OFF
        </Text>
        <Text style={[styles.offerDetails, { color: colors.primary[700] }]}>
          for the next 3 months on your {currentTier} plan
        </Text>
      </View>

      <View style={styles.buttonColumn}>
        <AnimatedPressable
          onPress={handleAcceptOffer}
          disabled={isLoading}
          style={[
            styles.button,
            styles.fullWidthButton,
            {
              backgroundColor: colors.primary[600],
            },
          ]}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <>
              <Gift size={18} color="#fff" />
              <Text style={[styles.buttonText, { color: '#fff', marginLeft: 8 }]}>
                Claim {discountPercent}% discount
              </Text>
            </>
          )}
        </AnimatedPressable>

        <AnimatedPressable
          onPress={handleDeclineOffer}
          disabled={isLoading}
          style={[
            styles.button,
            styles.fullWidthButton,
            {
              backgroundColor: 'transparent',
            },
          ]}
        >
          <Text style={[styles.buttonText, { color: colors.text.tertiary }]}>
            No thanks, continue canceling
          </Text>
        </AnimatedPressable>
      </View>
    </Animated.View>
  );

  const renderConfirmStep = () => (
    <Animated.View entering={SlideInUp.springify().damping(20)}>
      <View style={styles.stepHeader}>
        <View
          style={[
            styles.stepIconContainer,
            { backgroundColor: '#FEE2E2' },
          ]}
        >
          <AlertTriangle size={24} color="#DC2626" />
        </View>
        <Text style={[styles.stepTitle, { color: colors.text.primary }]}>
          Confirm cancellation
        </Text>
        <Text style={[styles.stepSubtitle, { color: colors.text.secondary }]}>
          Your subscription will remain active until{' '}
          <Text style={{ fontWeight: '600' }}>{formattedEndDate}</Text>. After
          that, you'll lose access to {currentTier} features.
        </Text>
      </View>

      <View
        style={[
          styles.warningBox,
          {
            backgroundColor: '#FEF3C7',
            borderColor: '#FCD34D',
          },
        ]}
      >
        <Text style={[styles.warningText, { color: '#92400E' }]}>
          You can reactivate your subscription anytime before {formattedEndDate}{' '}
          to keep your plan.
        </Text>
      </View>

      <View style={styles.buttonRow}>
        <AnimatedPressable
          onPress={handleClose}
          style={[
            styles.button,
            styles.secondaryButton,
            {
              backgroundColor: colors.surface.secondary,
            },
          ]}
        >
          <Text style={[styles.buttonText, { color: colors.text.primary }]}>
            Keep my plan
          </Text>
        </AnimatedPressable>

        <AnimatedPressable
          onPress={handleConfirmCancel}
          disabled={isLoading}
          style={[
            styles.button,
            styles.dangerButton,
            {
              backgroundColor: '#DC2626',
            },
          ]}
        >
          {isLoading ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={[styles.buttonText, { color: '#fff' }]}>
              Cancel subscription
            </Text>
          )}
        </AnimatedPressable>
      </View>
    </Animated.View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable
        style={styles.backdrop}
        accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
        onPress={() => {
          // The cancellation reason is free text; closing on the first backdrop
          // tap discarded what the user had written about why they are leaving.
          if (keyboardVisible) {
            Keyboard.dismiss();
            return;
          }
          handleClose();
        }}
      >
        <Pressable
          style={styles.contentWrapper}
          onPress={(e) => e.stopPropagation()}
        >
          <View
            style={[
              styles.container,
              {
                backgroundColor: colors.surface.primary,
                shadowColor: '#000',
              },
            ]}
          >
            {/* Close button */}
            <Pressable
              onPress={handleClose}
              style={[
                styles.closeButton,
                { backgroundColor: colors.surface.secondary },
              ]}
            >
              <X size={20} color={colors.text.secondary} />
            </Pressable>

            {/* Progress indicator */}
            <View style={styles.progressContainer}>
              {['reason', 'offer', 'confirm'].map((s, index) => (
                <View
                  key={s}
                  style={[
                    styles.progressDot,
                    {
                      backgroundColor:
                        step === s
                          ? colors.primary[600]
                          : step === 'confirm' && s !== 'confirm'
                          ? colors.primary[300]
                          : step === 'offer' && s === 'reason'
                          ? colors.primary[300]
                          : colors.border.light,
                    },
                  ]}
                />
              ))}
            </View>

            {/* Step content */}
            {step === 'reason' && renderReasonStep()}
            {step === 'offer' && renderOfferStep()}
            {step === 'confirm' && renderConfirmStep()}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 440,
  },
  container: {
    borderRadius: 24,
    padding: 28,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.15,
    shadowRadius: 30,
    elevation: 10,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 24,
  },
  progressDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  stepHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  stepIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  offerIconContainer: {
    backgroundColor: '#10B981',
    width: 64,
    height: 64,
    borderRadius: 32,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
  },
  reasonsContainer: {
    gap: 10,
    marginBottom: 16,
  },
  reasonOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 14,
    borderRadius: 12,
    borderWidth: 1.5,
  },
  reasonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  feedbackInput: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  buttonColumn: {
    gap: 12,
    marginTop: 8,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  primaryButton: {
    flex: 1,
  },
  secondaryButton: {
    flex: 1,
  },
  dangerButton: {
    flex: 1,
  },
  fullWidthButton: {
    width: '100%',
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  offerCard: {
    borderRadius: 16,
    borderWidth: 2,
    padding: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  offerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  offerTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  offerDiscount: {
    fontSize: 36,
    fontWeight: '800',
    marginBottom: 4,
  },
  offerDetails: {
    fontSize: 14,
    textAlign: 'center',
  },
  warningBox: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    marginBottom: 20,
  },
  warningText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
  },
});
